import { useState, useContext } from 'react';
import { Box, Link, Chip, Tooltip, Typography } from '@mui/material';
import {
    CheckCircleOutline,
    WarningAmberOutlined,
    AddCircleOutline,
    CalendarToday,
} from '@mui/icons-material';
import { format, parseISO, isBefore } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { AppContext } from './all';
import { formatPriceByLocale } from '../lib/utils';
import { getDateLocale } from '../lib/dateLocale';

type Fee = {
    id?: number;
    ini: string;
    price: number;
    status?: string;
    trash?: boolean;
    moment?: string;
    paymentForm?: string;
    feeslot?: {
        id: number;
    };
    priceShape?: {
        currency?: string;
        locale?: string;
    };
};

type Props = {
    fees?: Fee[];
    onFeeClicked?: (id?: number) => void;
    showUntil?: string;
    onGenerated?: (fee: Fee, index: number) => void;
};

/**
 *    Fee row
 *    =======
 *
 * Props
 *
 *     * array fees:
 *
 *         - double price
 *         - Date date: ISO date
 *         - string paymentForm
 *
 *  String (datetime) ini -> si una cuota es anterior a esta fecha, es que no hay que pintarla
 *  Function onFeeClicked callback
 *
 */
export function SttFeeRow({
    fees,
    onFeeClicked,
    showUntil,
    onGenerated,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [showMore, setShowMore] = useState<boolean>(false);

    /**
     * Generate fee
     */
    const generateFee = (fee: Fee, indexFee: number) => {
        if (!fee.feeslot) {
            return false;
        }

        cxt?.api('POST', `/feeslot/${fee.feeslot.id}/fees`, {
            params: { dates: [fee.ini.replace(' ', 'T')] },
            success: (response: { fees?: Fee[] }) => {
                if (typeof onGenerated === 'function' && response.fees?.[0]) {
                    onGenerated(response.fees[0], indexFee);
                }
            },
        });
    };

    let showFees: Fee[] | undefined;
    let seeMoreLink: boolean | undefined;

    if (fees && showUntil && !showMore) {
        showFees = fees.filter((fee: any) =>
            isBefore(parseISO(fee.ini), parseISO(showUntil)),
        );

        if (showFees.length === 0) {
            showFees = fees;
        }

        seeMoreLink = fees.length !== showFees.length;
    } else {
        showFees = fees;
    }

    const isPaid = (fee: Fee) => fee.status === 'PAID';
    const isPending = (fee: Fee) =>
        fee.id && !fee.trash && fee.status !== 'PAID';
    const isGenerable = (fee: Fee) => !fee.id || fee.trash;

    // -----| Render |-----
    return (
        <>
            {showFees && (
                <Box display="flex" flexWrap="wrap" gap={1.5}>
                    {showFees.map((fee: any, idx: any) => {
                        if (
                            (!fee.moment || fee.moment !== 'BLANK') &&
                            Number.parseFloat(String(fee.price)) > 0
                        ) {
                            const paid = isPaid(fee);
                            const pending = isPending(fee);
                            const generable = isGenerable(fee);

                            return (
                                <Tooltip
                                    key={fee?.id ?? `${fee?.id}-${fee?.ini}`}
                                    title={
                                        paid
                                            ? t('fee.paid')
                                            : pending
                                              ? t('fee.pending')
                                              : t('fee.notGenerated')
                                    }
                                    arrow
                                >
                                    <Box
                                        onClick={() => {
                                            if (!generable) {
                                                onFeeClicked?.(
                                                    !fee.trash
                                                        ? fee.id
                                                        : undefined,
                                                );
                                            }
                                        }}
                                        sx={(theme) => ({
                                            minWidth: 120,
                                            p: 1.5,
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: paid
                                                ? 'success.main'
                                                : pending
                                                  ? 'error.main'
                                                  : 'grey.300',
                                            backgroundColor: paid
                                                ? 'success.light'
                                                : pending
                                                  ? 'error.light'
                                                  : 'grey.50',
                                            ...theme.applyStyles('dark', {
                                                backgroundColor: paid
                                                    ? 'success.dark'
                                                    : pending
                                                      ? 'error.dark'
                                                      : 'background.default',
                                                ...(!paid &&
                                                    !pending && {
                                                        borderColor: 'divider',
                                                    }),
                                            }),
                                            cursor: generable
                                                ? 'default'
                                                : 'pointer',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                transform: generable
                                                    ? 'none'
                                                    : 'translateY(-2px)',
                                                boxShadow: generable
                                                    ? 'none'
                                                    : '0 4px 12px rgba(0,0,0,0.15)',
                                            },
                                        })}
                                    >
                                        {/* Status Icon + Date */}
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={0.5}
                                            mb={0.5}
                                        >
                                            {paid && (
                                                <CheckCircleOutline
                                                    sx={{
                                                        fontSize: 16,
                                                        color: 'success.main',
                                                    }}
                                                />
                                            )}
                                            {pending && (
                                                <WarningAmberOutlined
                                                    sx={{
                                                        fontSize: 16,
                                                        color: 'error.main',
                                                    }}
                                                />
                                            )}
                                            {generable && (
                                                <CalendarToday
                                                    sx={{
                                                        fontSize: 14,
                                                        color: 'grey.500',
                                                    }}
                                                />
                                            )}
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: paid
                                                        ? 'success.main'
                                                        : pending
                                                          ? 'error.main'
                                                          : 'text.secondary',
                                                }}
                                            >
                                                {format(
                                                    parseISO(fee.ini),
                                                    'd MMM yy',
                                                    { locale: getDateLocale() },
                                                )}
                                            </Typography>
                                        </Box>

                                        {/* Price */}
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 700,
                                                color: paid
                                                    ? 'success.main'
                                                    : pending
                                                      ? 'error.main'
                                                      : 'text.primary',
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            {formatPriceByLocale(
                                                fee.price,
                                                fee?.priceShape?.currency,
                                                fee?.priceShape?.locale,
                                            )}
                                        </Typography>

                                        {/* Payment Form or Generate Button */}
                                        {fee.id && !fee.trash ? (
                                            fee.paymentForm && (
                                                <Chip
                                                    label={t(
                                                        `PF.${fee.paymentForm}`,
                                                    )}
                                                    size="small"
                                                    variant="outlined"
                                                    color={
                                                        paid
                                                            ? 'success'
                                                            : 'error'
                                                    }
                                                    sx={{
                                                        mt: 0.5,
                                                        height: 20,
                                                        fontSize: 10,
                                                    }}
                                                />
                                            )
                                        ) : (
                                            <Link
                                                component="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    generateFee(fee, idx);
                                                }}
                                                sx={{
                                                    mt: 0.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    fontSize: 12,
                                                    color: 'var(--brand-text)',
                                                    textDecoration: 'none',
                                                    '&:hover': {
                                                        textDecoration:
                                                            'underline',
                                                    },
                                                }}
                                            >
                                                <AddCircleOutline
                                                    sx={{ fontSize: 14 }}
                                                />
                                                {t('Generate')}
                                            </Link>
                                        )}
                                    </Box>
                                </Tooltip>
                            );
                        }
                        return null;
                    })}
                </Box>
            )}

            {!showMore && seeMoreLink && (
                <Box
                    mt={2}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Link
                        component="button"
                        sx={{ fontSize: 13 }}
                        onClick={() => setShowMore(true)}
                    >
                        {`${t('fee.showAll')}...`}
                    </Link>
                </Box>
            )}
        </>
    );
}
